/* global BigInt */
/**
 * User Position Panel Component
 * Displays user's position and P&L for the active game
 */

import React from 'react';
import './UserPositionPanel.css';
import {
  convertAtomsToDisplay,
  convertLamportsToDisplay,
  convertAvgEntryToDisplay,
} from '../helpers/positionMath';

function formatTimestamp(timestamp) {
  if (!timestamp) {
    return 'N/A';
  }

  try {
    const date = new Date(timestamp);
    return date.toLocaleString();
  } catch {
    return 'N/A';
  }
}

function UserPositionPanel({
  walletConnected,
  position,
  loading,
  error,
  currentValue,
  unrealizedPnL,
  unrealizedPnLPercent,
}) {
  // Empty state: no wallet
  if (!walletConnected) {
    return (
      <article className="position-panel">
        <div className="position-panel__header">
          <h3 className="position-panel__title">Your Position</h3>
        </div>
        <div className="position-panel__empty">
          <p>Connect wallet to view your position</p>
        </div>
      </article>
    );
  }

  // Loading state
  if (loading) {
    return (
      <article className="position-panel">
        <div className="position-panel__header">
          <h3 className="position-panel__title">Your Position</h3>
        </div>
        <div className="position-panel__empty">
          <p>Loading position...</p>
        </div>
      </article>
    );
  }

  // Error state
  if (error) {
    return (
      <article className="position-panel">
        <div className="position-panel__header">
          <h3 className="position-panel__title">Your Position</h3>
        </div>
        <div className="position-panel__empty position-panel__empty--error">
          <p>{error}</p>
        </div>
      </article>
    );
  }

  // Empty state: no position yet (never traded in this game)
  if (!position || window.BigInt(position.total_tokens_bought || 0) === 0n) {
    return (
      <article className="position-panel">
        <div className="position-panel__header">
          <h3 className="position-panel__title">Your Position</h3>
        </div>
        <div className="position-panel__empty">
          <p>No position yet. Buy tokens to start tracking your P&L.</p>
        </div>
      </article>
    );
  }

  // Position data - convert int8/numeric to strings for display functions
  const tokenBalance = convertAtomsToDisplay(String(position.token_balance || 0));
  const avgEntry = convertAvgEntryToDisplay(String(position.avg_entry_lamports_per_token || 0));
  const costBasis = convertLamportsToDisplay(String(position.cost_basis_lamports || 0), 4);
  const realizedPnL = convertLamportsToDisplay(String(position.realized_pnl_lamports || 0), 4);
  
  // If position is closed (no tokens left), unrealized P&L is 0
  const isPositionClosed = window.BigInt(position.token_balance || 0) === 0n;
  const currentVal = isPositionClosed ? '0.0000' : (currentValue ? convertLamportsToDisplay(currentValue, 4) : 'N/A');
  const unrealizedPnLValue = isPositionClosed ? '0.0000' : (unrealizedPnL ? convertLamportsToDisplay(unrealizedPnL, 4) : 'N/A');
  const unrealizedPnLPercentValue = isPositionClosed ? '0.00%' : 
    (unrealizedPnLPercent !== null && unrealizedPnLPercent !== undefined
      ? `${unrealizedPnLPercent >= 0 ? '+' : ''}${unrealizedPnLPercent.toFixed(2)}%`
      : 'N/A');

  // Determine P&L styling
  const unrealizedPnLBigInt = unrealizedPnL ? window.BigInt(unrealizedPnL) : 0n;
  const realizedPnLBigInt = window.BigInt(position.realized_pnl_lamports || 0);
  const unrealizedPnLClass = unrealizedPnLBigInt > 0n ? 'positive' : unrealizedPnLBigInt < 0n ? 'negative' : 'neutral';
  const realizedPnLClass = realizedPnLBigInt > 0n ? 'positive' : realizedPnLBigInt < 0n ? 'negative' : 'neutral';

  const lastUpdated = formatTimestamp(position.updated_at);
  const statusClass = position.position_status === 'closed' ? 'position-panel__status--closed' : '';

  return (
    <article className="position-panel">
      <div className="position-panel__header">
        <div className="position-panel__header-left">
          <h3 className="position-panel__title">Your Position</h3>
          <span className="position-panel__timestamp">{lastUpdated}</span>
        </div>
        <span className={`position-panel__status ${statusClass}`}>{position.position_status || 'open'}</span>
      </div>

      <div className="position-panel__grid">
        <div className="position-metric">
          <span className="position-metric__label">Cost Basis</span>
          <strong className="position-metric__value">{costBasis} SOL</strong>
        </div>

        <div className="position-metric">
          <span className="position-metric__label">Current Value</span>
          <strong className="position-metric__value">{currentVal} SOL</strong>
        </div>

        <div className="position-metric">
          <span className="position-metric__label">Unrealized P&L</span>
          <strong className={`position-metric__value position-metric__value--${unrealizedPnLClass}`}>
            {unrealizedPnLValue} SOL
          </strong>
        </div>

        <div className="position-metric">
          <span className="position-metric__label">Unrealized P&L %</span>
          <strong className={`position-metric__value position-metric__value--${unrealizedPnLClass}`}>
            {unrealizedPnLPercentValue}
          </strong>
        </div>

        <div className="position-metric">
          <span className="position-metric__label">Realized P&L</span>
          <strong className={`position-metric__value position-metric__value--${realizedPnLClass}`}>
            {realizedPnL} SOL
          </strong>
        </div>
      </div>
    </article>
  );
}

export default UserPositionPanel;
